/* ============================================================
   ĒTHA — Dosha Finder popup

   The popup is closed by default and has no timer, no scroll trigger,
   no exit intent and no first-visit behaviour. It opens on exactly two
   things and nothing else:

     • a click on any element carrying  data-etha-dosha-finder-open
     • a link whose hash is            #etha-dosha-finder
       (arriving with that hash on the URL counts, once, and the hash is
        then removed from the address bar so the Back button stays honest)

   No dependencies. Nothing is loaded from anywhere.
   ============================================================ */

(function () {
  'use strict';

  var OPEN_TOKEN = '#etha-dosha-finder';
  var DOSHAS = ['vata', 'pitta', 'kapha'];
  var QUESTION_KEYS = ['energy', 'body', 'appetite', 'skin'];

  /* When two Doshas tie at 2–2 the steadier trait decides, in this order.
     A third and fourth entry can never be reached by four answers over three
     Doshas — the only possible tie is two-and-two — but they are the approved
     order and they cost nothing to keep. */
  var TIE_BREAK = ['skin', 'appetite', 'energy', 'body'];

  var SCREEN_ORDER = ['q1', 'q2', 'q3', 'q4'];
  var LOCK_CLASS = 'etha-df-scroll-locked';

  /* ---------------------------------------------------------
     The scoring. Deliberately the only thing in this file that
     does arithmetic, so it can be read — and tested — on its own.
     Each answer is one point. The highest score is the result.
     Never a combined result, never a stronger claim than the copy makes.
     --------------------------------------------------------- */

  function scoreAnswers(answers) {
    var counts = { vata: 0, pitta: 0, kapha: 0 };
    var i;

    for (i = 0; i < QUESTION_KEYS.length; i++) {
      var chosen = answers[QUESTION_KEYS[i]];
      if (Object.prototype.hasOwnProperty.call(counts, chosen)) counts[chosen] += 1;
    }

    var highest = Math.max(counts.vata, counts.pitta, counts.kapha);
    var tied = DOSHAS.filter(function (dosha) { return counts[dosha] === highest; });

    if (tied.length === 1) {
      return { dosha: tied[0], counts: counts, tieBroken: false };
    }

    for (i = 0; i < TIE_BREAK.length; i++) {
      var answer = answers[TIE_BREAK[i]];
      if (tied.indexOf(answer) !== -1) {
        return { dosha: answer, counts: counts, tieBroken: true };
      }
    }

    return { dosha: tied[0], counts: counts, tieBroken: true };
  }

  /* ---------------------------------------------------------
     Analytics. Three destinations, none of them a library and none of them
     carrying anything personal — only which screen, which answer, which Dosha.

     1. Shopify.analytics.publish — the store's actual analytics surface. This
        store has no GTM container and no gtag (verified on the storefront:
        window.google_tag_manager is undefined), so Customer Events is the only
        route to GA4. A custom pixel in Settings → Customer events subscribes to
        these six names and forwards them; the snippet is in
        docs/handoff/dosha-finder-analytics.md. Shopify runs the pixel behind the
        shop's own consent state, which is why nothing here checks consent.
     2. window.dataLayer — harmless and already correct for the day a GTM
        container does land.
     3. a DOM event of the same name, for anything else on the page.
     --------------------------------------------------------- */

  function track(name, detail) {
    var payload = detail || {};
    var entry = { event: name };
    var key;

    for (key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) entry[key] = payload[key];
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(entry);

    try {
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (error) {
      /* An analytics destination must never take the popup down with it. */
    }

    try {
      document.dispatchEvent(new CustomEvent(name, { detail: payload }));
    } catch (error) {
      /* CustomEvent is present everywhere this ships; a failure here must
         never take the popup down with it. */
    }
  }

  /* ---------------------------------------------------------
     Background scroll, locked and restored
     --------------------------------------------------------- */

  var savedScrollY = 0;
  var scrollLocked = false;

  function lockScroll() {
    if (scrollLocked) return;
    savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.top = (-savedScrollY) + 'px';
    document.body.classList.add(LOCK_CLASS);
    scrollLocked = true;
  }

  function unlockScroll() {
    if (!scrollLocked) return;
    document.body.classList.remove(LOCK_CLASS);
    document.body.style.top = '';
    scrollLocked = false;
    window.scrollTo(0, savedScrollY);
  }

  /* ---------------------------------------------------------
     One popup
     --------------------------------------------------------- */

  function createFinder(root) {
    var screens = {};
    var opened = false;
    var current = null;
    var answers = {};
    var opener = null;
    var pointerDownTarget = null;
    var warmed = false;
    var advanceTimer = null;

    var live = root.querySelector('[data-df-live]');
    var ctaTemplate = root.querySelector('[data-df-cta-template]');
    var nativeDialog = typeof root.showModal === 'function';

    Array.prototype.forEach.call(root.querySelectorAll('[data-df-screen]'), function (node) {
      screens[node.getAttribute('data-df-screen')] = node;
    });

    /* -------- photographs --------
       Every photograph is lazy, and the dialog is display:none until it opens,
       so none of them cost the storefront anything on page load. The moment
       the popup opens, the screens still hidden are switched to eager: a
       hidden eager image does load, which is what makes question 2 arrive
       before the visitor does. */

    function warmPhoto(screenName) {
      var screen = screens[screenName];
      if (!screen) return;
      Array.prototype.forEach.call(screen.querySelectorAll('img[data-df-photo]'), function (img) {
        img.loading = 'eager';
      });
    }

    function warmAllPhotos() {
      if (warmed) return;
      warmed = true;
      Object.keys(screens).forEach(warmPhoto);
    }

    /* -------- the call to action --------
       The aura button's animation loop lives for as long as its element is in
       the document, so the button is put into the result on screen and taken
       out again on the way out. That is what keeps one loop running, and only
       while it can be seen. The vendored component notices the new node by
       itself and starts it; removing the node ends it on the next frame. */

    function clearCta() {
      Array.prototype.forEach.call(root.querySelectorAll('[data-df-cta-slot]'), function (slot) {
        while (slot.firstChild) slot.removeChild(slot.firstChild);
      });
    }

    function placeCta(screenName) {
      clearCta();
      if (!ctaTemplate) return;

      var screen = screens[screenName];
      if (!screen) return;
      var slot = screen.querySelector('[data-df-cta-slot]');
      if (!slot) return;

      var button = ctaTemplate.content.firstElementChild.cloneNode(true);
      button.setAttribute('href', slot.getAttribute('data-df-cta-href'));
      button.setAttribute('data-df-collection', slot.getAttribute('data-df-cta-dosha'));
      slot.appendChild(button);
    }

    /* -------- screens -------- */

    function announce(name) {
      if (!live) return;
      var index = SCREEN_ORDER.indexOf(name);
      if (index !== -1) {
        live.textContent = 'Question ' + (index + 1) + ' of 4.';
      } else {
        live.textContent = 'Your result.';
      }
    }

    function showScreen(name) {
      if (!screens[name]) return;

      Object.keys(screens).forEach(function (key) {
        screens[key].hidden = key !== name;
      });
      current = name;

      clearChosen();
      placeCta(name);

      var body = screens[name].querySelector('.etha-df__body');
      if (body) body.scrollTop = 0;

      var focusTarget = screens[name].querySelector('[data-df-focus]');
      if (focusTarget) focusTarget.focus({ preventScroll: true });

      announce(name);
    }

    function reset() {
      answers = {};
      showScreen('q1');
    }

    function clearChosen() {
      Array.prototype.forEach.call(root.querySelectorAll('[data-df-chosen]'), function (button) {
        button.removeAttribute('data-df-chosen');
      });
    }

    function cancelAdvance() {
      if (advanceTimer === null) return;
      window.clearTimeout(advanceTimer);
      advanceTimer = null;
    }

    function prefersReducedMotion() {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    /* -------- opening and closing -------- */

    function isOpen() { return opened; }

    /* The closing animation runs on the dialog itself, so the dialog cannot be
       taken out of the document until it has finished. Everything that is not
       visual — state, scroll, focus, the event — still happens the moment close
       is asked for; only the teardown waits. EXIT_MS matches
       etha-df-card-out / etha-df-veil-out in the stylesheet. */
    var EXIT_MS = 280;
    var exitTimer = null;

    function teardown() {
      exitTimer = null;
      root.removeAttribute('data-df-closing');

      if (nativeDialog) {
        if (root.open) root.close();
      } else {
        root.removeAttribute('data-df-fallback-open');
      }

      clearCta();
    }

    function open(trigger, source) {
      if (opened) return;

      /* Reopened mid-exit: finish the teardown now, so the opening animation
         starts from a closed card rather than resuming a half-faded one. */
      if (exitTimer !== null) {
        window.clearTimeout(exitTimer);
        teardown();
      }

      opener = trigger || document.activeElement;
      opened = true;

      lockScroll();

      if (nativeDialog) {
        if (!root.open) root.showModal();
      } else {
        root.setAttribute('data-df-fallback-open', '');
        root.setAttribute('role', 'dialog');
        root.setAttribute('aria-modal', 'true');
        document.addEventListener('keydown', fallbackKeydown, true);
      }

      reset();
      warmAllPhotos();
      track('etha_dosha_finder_open', { trigger: source || 'unknown' });
    }

    function close() {
      if (!opened) return;

      var closedFrom = current;
      opened = false;
      cancelAdvance();

      if (!nativeDialog) document.removeEventListener('keydown', fallbackKeydown, true);

      if (prefersReducedMotion()) {
        teardown();
      } else {
        root.setAttribute('data-df-closing', '');
        if (exitTimer !== null) window.clearTimeout(exitTimer);
        exitTimer = window.setTimeout(teardown, EXIT_MS);
      }

      unlockScroll();
      if (live) live.textContent = '';
      track('etha_dosha_finder_close', { screen: closedFrom });

      if (opener && document.contains(opener) && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      }
      opener = null;
    }

    /* -------- answering -------- */

    function answer(key, dosha, button) {
      if (advanceTimer !== null) return;   /* a second tap during the pause */

      answers[key] = dosha;
      if (button) button.setAttribute('data-df-chosen', '');

      var index = QUESTION_KEYS.indexOf(key);
      track('etha_dosha_finder_answer', {
        question: index + 1,
        question_key: key,
        answer: dosha
      });

      var go = function () {
        advanceTimer = null;
        if (index < SCREEN_ORDER.length - 1) showScreen(SCREEN_ORDER[index + 1]);
        else finish();
      };

      /* long enough to see the pin fill, short enough to still feel like the
         answer moved you on by itself */
      if (prefersReducedMotion()) go();
      else advanceTimer = window.setTimeout(go, 240);
    }

    function finish() {
      var result = scoreAnswers(answers);
      showScreen('result-' + result.dosha);
      track('etha_dosha_finder_result', {
        dosha: result.dosha,
        score_vata: result.counts.vata,
        score_pitta: result.counts.pitta,
        score_kapha: result.counts.kapha,
        tie_broken: result.tieBroken
      });
    }

    /* -------- events inside the popup -------- */

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var answerButton = target.closest('[data-df-answer]');
      if (answerButton && root.contains(answerButton)) {
        answer(answerButton.getAttribute('data-df-key'), answerButton.getAttribute('data-df-answer'), answerButton);
        return;
      }

      if (target.closest('[data-df-close]')) {
        close();
        return;
      }

      var collectionLink = target.closest('[data-df-collection]');
      if (collectionLink) {
        track('etha_dosha_finder_collection_click', {
          dosha: collectionLink.getAttribute('data-df-collection')
        });
        return;
      }

      if (target.closest('[data-df-quiz]')) {
        track('etha_dosha_finder_full_quiz_click', { screen: current });
        return;
      }

      /* Nothing inside the panel was clicked, so this is the backdrop.
         Only reachable on desktop, where the panel does not fill the
         dialog — and only when the press started there too, so a
         selection dragged out of the panel does not close it. */
      if (event.target === root && pointerDownTarget === root) close();
    });

    root.addEventListener('pointerdown', function (event) {
      pointerDownTarget = event.target;
    });

    /* Escape. The native dialog closes itself, so the work is to run the
       same cleanup either way rather than to close it twice. */
    root.addEventListener('cancel', function (event) {
      event.preventDefault();
      close();
    });

    /* A dialog's close event is fired as a queued task, so one from a close
       that already ran its cleanup can land after the popup has been opened
       again. Anything still open is therefore not what this event is about. */
    root.addEventListener('close', function () {
      if (!opened || root.open) return;
      close();
    });

    function fallbackKeydown(event) {
      if (!opened) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      var items = Array.prototype.filter.call(
        root.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        function (node) { return node.offsetParent !== null || node === document.activeElement; }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    return {
      root: root,
      open: open,
      close: close,
      isOpen: isOpen,
      warmFirst: function () { warmPhoto('q1'); }
    };
  }

  /* ---------------------------------------------------------
     Finding the popup. The controller is cached on the element, so a
     theme-editor section reload simply hands back a fresh one instead of
     leaving stale listeners behind.
     --------------------------------------------------------- */

  function getFinder() {
    var root = document.querySelector('[data-etha-dosha-finder]');
    if (!root) return null;
    if (!root.ethaDoshaFinder) root.ethaDoshaFinder = createFinder(root);
    return root.ethaDoshaFinder;
  }

  function openFinder(trigger, source) {
    var finder = getFinder();
    if (finder) finder.open(trigger, source);
  }

  function closeFinder() {
    var finder = getFinder();
    if (finder) finder.close();
  }

  /* ---------------------------------------------------------
     Triggers
     --------------------------------------------------------- */

  function linkAsksForFinder(link) {
    return link.hash === OPEN_TOKEN;
  }

  function linkIsThisPage(link) {
    return link.protocol === window.location.protocol &&
           link.host === window.location.host &&
           link.pathname === window.location.pathname &&
           link.search === window.location.search;
  }

  function stripHash() {
    if (window.location.hash !== OPEN_TOKEN) return;
    if (!window.history || typeof window.history.replaceState !== 'function') return;
    try {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch (error) {
      /* Some contexts refuse replaceState. Leaving the hash on the URL is a
         cosmetic loss; failing to open the popup would not be. */
    }
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var attributeTrigger = target.closest('[data-etha-dosha-finder-open]');
    if (attributeTrigger) {
      event.preventDefault();
      openFinder(attributeTrigger, 'attribute');
      return;
    }

    var link = target.closest('a[href]');
    if (!link || !linkAsksForFinder(link)) return;

    /* A link to this same page opens the popup where it stands. A link to a
       different page is left alone: it navigates, and the hash opens the
       popup when it lands. */
    if (linkIsThisPage(link)) {
      event.preventDefault();
      openFinder(link, 'link');
    }
  });

  /* A hint that the popup is about to be wanted — the first question's
     photograph starts loading before the click lands. */
  function warmOnIntent(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-etha-dosha-finder-open], a[href]')) return;

    var trigger = target.closest('[data-etha-dosha-finder-open]');
    var link = target.closest('a[href]');
    if (!trigger && !(link && linkAsksForFinder(link))) return;

    var finder = getFinder();
    if (finder) finder.warmFirst();
  }

  document.addEventListener('pointerenter', warmOnIntent, true);
  document.addEventListener('focusin', warmOnIntent);

  window.addEventListener('hashchange', function () {
    if (window.location.hash !== OPEN_TOKEN) return;
    stripHash();
    openFinder(null, 'hash');
  });

  function openFromLandingHash() {
    if (window.location.hash !== OPEN_TOKEN) return;
    stripHash();
    openFinder(null, 'hash');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openFromLandingHash);
  } else {
    openFromLandingHash();
  }

  /* ---------------------------------------------------------
     Theme editor. Selecting the section previews the popup; deselecting
     or reloading the section puts everything back, scroll lock included.
     --------------------------------------------------------- */

  document.addEventListener('shopify:section:select', function (event) {
    if (event.target && event.target.querySelector('[data-etha-dosha-finder]')) {
      openFinder(null, 'theme-editor');
    }
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    if (event.target && event.target.querySelector('[data-etha-dosha-finder]')) closeFinder();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target && event.target.querySelector('[data-etha-dosha-finder]')) {
      closeFinder();
      unlockScroll();
    }
  });

  /* Opened, closed and scored from anywhere — the banner snippet uses the
     data attribute, not this, but QA and any future theme code can. */
  window.EthaDoshaFinder = {
    open: function (source) { openFinder(null, source || 'api'); },
    close: closeFinder,
    score: scoreAnswers
  };
}());
